import React, { lazy, Suspense } from "react";
import { LazyMotion, domAnimation } from "framer-motion";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useParams, Navigate as RouterNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import RouteSeo from "@/components/common/RouteSeo";
const CityLanding = lazy(() => import("@/pages/CityLanding"));
const StateLanding = lazy(() => import("@/pages/StateLanding"));
const GuideDetail = lazy(() => import("@/pages/Guides"));
const GuideList = lazy(() => import("@/pages/Guides").then((m) => ({ default: m.GuideList })));
import Layout from "@/components/layout/Layout";
import ScrollToTop from "@/components/common/ScrollToTop";
import Home from "@/pages/Home";
const Products = lazy(() => import("@/pages/Products"));
const ProductDetail = lazy(() => import("@/pages/ProductDetailNeu"));
const ProductFinder = lazy(() => import("@/pages/ProductFinder"));
const Compare = lazy(() => import("@/pages/Compare"));
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const ProductionScenesPreview = lazy(() => import("@/pages/ProductionScenesPreview"));
const Showroom = lazy(() => import("@/pages/Showroom"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Contact = lazy(() => import("@/pages/Contact"));
const Support = lazy(() => import("@/pages/Support"));
const Accessories = lazy(() => import("@/pages/Accessories"));
const AccessoriesLanding = lazy(() => import("@/pages/AccessoriesShop"));
const AccessoryListing = lazy(() => import("@/pages/AccessoriesShop").then((m) => ({ default: m.AccessoryListing })));
const AccessoryDetail = lazy(() => import("@/pages/AccessoriesShop").then((m) => ({ default: m.AccessoryDetail })));
const AccessoryFinder = lazy(() => import("@/pages/AccessoriesShop").then((m) => ({ default: m.AccessoryFinder })));
const About = lazy(() => import("@/pages/About"));
import { CartProvider } from "@/lib/cart";
import { AuthProvider } from "@/lib/auth";
import { ConsentProvider, ConsentBanner } from "@/lib/consent";
const Login = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.Login })));
const Register = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.Register })));
const Protected = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.Protected })));
const AccountLayout = lazy(() => import("@/pages/Account").then((m) => ({ default: m.AccountLayout })));
const AccountOverview = lazy(() => import("@/pages/Account").then((m) => ({ default: m.AccountOverview })));
const AccountOrders = lazy(() => import("@/pages/Account").then((m) => ({ default: m.AccountOrders })));
const AccountOrderDetail = lazy(() => import("@/pages/Account").then((m) => ({ default: m.AccountOrderDetail })));
const AccountProfile = lazy(() => import("@/pages/Account").then((m) => ({ default: m.AccountProfile })));
const AccountLinkOrder = lazy(() => import("@/pages/Account").then((m) => ({ default: m.AccountLinkOrder })));
const AccountSubscriptions = lazy(() => import("@/pages/Account").then((m) => ({ default: m.AccountSubscriptions })));
const AdminLayout = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminDashboard })));
const AdminOrders = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminOrders })));
const AdminOrderDetail = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminOrderDetail })));
const AdminCustomers = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminCustomers })));
const AdminCustomerDetail = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminCustomerDetail })));
const AdminWorkorders = lazy(() => import("@/pages/AdminOps").then((m) => ({ default: m.AdminWorkorders })));
const AdminWorkorderDetail = lazy(() => import("@/pages/AdminOps").then((m) => ({ default: m.AdminWorkorderDetail })));
const AdminTasks = lazy(() => import("@/pages/AdminOps").then((m) => ({ default: m.AdminTasks })));
const AdminCalendar = lazy(() => import("@/pages/AdminOps").then((m) => ({ default: m.AdminCalendar })));
const AdminOpsSettings = lazy(() => import("@/pages/AdminOps").then((m) => ({ default: m.AdminOpsSettings })));
const AdminSecurity = lazy(() => import("@/pages/AdminSecurity"));
const AdminFotoKreulCatalog = lazy(() => import("@/pages/AdminFotoKreulCatalog"));
const AdminOrderFromDocument = lazy(() => import("@/pages/AdminOrderFromDocument"));
const Werkstatt = lazy(() => import("@/pages/Werkstatt"));
const Live = lazy(() => import("@/pages/Live"));
const AdminProducts = lazy(() => import("@/pages/AdminOpsProducts").then((m) => ({ default: m.AdminProducts })));
const AdminProductDetail = lazy(() => import("@/pages/AdminOpsProducts").then((m) => ({ default: m.AdminProductDetail })));
const AdminSupplierDetail = lazy(() => import("@/pages/AdminOpsProducts").then((m) => ({ default: m.AdminSupplierDetail })));
const AdminComponents = lazy(() => import("@/pages/AdminComponents").then((m) => ({ default: m.AdminComponents })));
const AdminComponentDetail = lazy(() => import("@/pages/AdminComponents").then((m) => ({ default: m.AdminComponentDetail })));
const AdminQuotes = lazy(() => import("@/pages/AdminQuotes").then((m) => ({ default: m.AdminQuotes })));
const AdminQuoteDetail = lazy(() => import("@/pages/AdminQuotes").then((m) => ({ default: m.AdminQuoteDetail })));
const AdminDocumentSettings = lazy(() => import("@/pages/AdminDocumentSettings").then((m) => ({ default: m.AdminDocumentSettings })));
const AdminPaymentFees = lazy(() => import("@/pages/AdminPaymentFees"));
const AdminIntlShipping = lazy(() => import("@/pages/AdminIntlShipping"));
const AdminEinkauf = lazy(() => import("@/pages/AdminEinkauf"));
const EinkaufApp = lazy(() => import("@/pages/EinkaufApp"));
const Zentrale = lazy(() => import("@/pages/Zentrale"));
const AdminNordfotoHealth = lazy(() => import("@/pages/AdminNordfotoHealth"));
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
const QuoteView = lazy(() => import("@/pages/QuoteView"));
const AdminPagesList = lazy(() => import("@/pages/AdminPages").then((m) => ({ default: m.AdminPagesList })));
const AdminPageEditor = lazy(() => import("@/pages/AdminPages").then((m) => ({ default: m.AdminPageEditor })));
const AdminAccessories = lazy(() => import("@/pages/AdminAccessories"));
const AdminAccessoryEdit = lazy(() => import("@/pages/AdminAccessories").then((m) => ({ default: m.AdminAccessoryEdit })));
const AdminSubscriptions = lazy(() => import("@/pages/AdminSubscriptions"));
const Bestpreis = lazy(() => import("@/pages/Bestpreis"));
const AdminBestpreis = lazy(() => import("@/pages/AdminBestpreis"));
const AdminWithdrawals = lazy(() => import("@/pages/AdminWithdrawals"));
const AdminSiteConfig = lazy(() => import("@/pages/AdminSiteConfig"));
const AdminPromotion = lazy(() => import("@/pages/AdminPromotion"));
const AdminCrossSelling = lazy(() => import("@/pages/AdminCrossSelling"));
const MagicMirrorRahmenListing = lazy(() => import("@/pages/MagicMirrorRahmenListing"));
const MagicMirrorRahmenDetail = lazy(() => import("@/pages/MagicMirrorRahmenDetail"));
import BestpreisFloatingBanner from "@/components/BestpreisFloatingBanner";
import { LocaleProvider } from "@/locale/LocaleContext";
const Impressum = lazy(() => import("@/pages/legal/Impressum"));
const Datenschutz = lazy(() => import("@/pages/legal/Datenschutz"));
const AGB = lazy(() => import("@/pages/legal/AGB"));
const Widerrufsbelehrung = lazy(() => import("@/pages/legal/Widerrufsbelehrung"));
const Kuendigung = lazy(() => import("@/pages/Kuendigung"));
const Withdrawal = lazy(() => import("@/pages/Withdrawal"));
const AcademyLanding = lazy(() => import("@/pages/AcademyLanding"));
const KreulBooth = lazy(() => import("@/pages/KreulBooth"));
const Zahlungsarten = lazy(() => import("@/pages/Zahlungsarten"));
const Gewaehrleistung = lazy(() => import("@/pages/legal/Gewaehrleistung"));
// Sprachbereiche /en und /nl: gemeinsame Seiten, Texte je Sprache in eigenem Chunk (LocaleProvider lädt sie nach)
const IntlHome = lazy(() => import("@/pages/intl/IntlHome"));
const IntlProducts = lazy(() => import("@/pages/intl/IntlProducts"));
const IntlProductDetail = lazy(() => import("@/pages/intl/IntlProductDetail"));
const IntlWhyUs = lazy(() => import("@/pages/intl/IntlWhyUs"));
const IntlFaq = lazy(() => import("@/pages/intl/IntlFaq"));
const IntlContact = lazy(() => import("@/pages/intl/IntlContact"));
const IntlCity = lazy(() => import("@/pages/intl/IntlCity"));
const intl = (lang, Page) => <LocaleProvider key={lang} lang={lang}><Page /></LocaleProvider>;

// Client-side 301 replacement: alte URLs /products/:slug → /produkte/:slug
// (Sitemap-Ping und robots.txt sowie serverseitige 301-Header werden separat
// über /api/legacy-redirect gelöst.)
function LegacyProductRedirect() {
  const { slug } = useParams();
  return <RouterNavigate to={`/produkte/${slug}`} replace />;
}

function App() {
  return (
    <div className="App">
      <LazyMotion features={domAnimation}>
      <HelmetProvider>
        <AuthProvider>
          <ConsentProvider>
            <CartProvider>
              <BrowserRouter>
                <ScrollToTop />
                <RouteSeo />
                <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
                <Routes>
                  <Route element={<Layout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/fotobox-kaufen/:city" element={<CityLanding />} />
                    <Route path="/bundesland/:state" element={<StateLanding />} />
                    <Route path="/ratgeber" element={<GuideList />} />
                    <Route path="/ratgeber/:slug" element={<GuideDetail />} />
                    <Route path="/fotoboxen" element={<Products />} />
                    <Route path="/produkte" element={<Navigate to="/fotoboxen" replace />} />
                    <Route path="/produkte/:slug" element={<ProductDetail />} />
                    <Route path="/entwurf/produkt/:slug" element={<ProductDetail />} />
                    {/* SEO: alte /products/* URLs dauerhaft auf /produkte/* leiten */}
                    <Route path="/products" element={<Navigate to="/fotoboxen" replace />} />
                    <Route path="/products/:slug" element={<LegacyProductRedirect />} />
                  <Route path="/produktfinder" element={<ProductFinder />} />
                  <Route path="/vergleich" element={<Compare />} />
                  <Route path="/zubehoer" element={<AccessoriesLanding />} />
                  <Route path="/zubehoer/drucker" element={<AccessoryListing category="drucker" />} />
                  <Route path="/zubehoer/verbrauchsmaterial" element={<AccessoryListing category="verbrauchsmaterial" />} />
                  <Route path="/zubehoer/verbrauchsmaterial/finder" element={<AccessoryFinder />} />
                  <Route path="/zubehoer/hintergrundsysteme" element={<AccessoryListing category="hintergrundsysteme" />} />
                  <Route path="/zubehoer/transportloesungen" element={<AccessoryListing category="transportloesungen" />} />
                  <Route path="/zubehoer/ersatzteile" element={<AccessoryListing category="ersatzteile" />} />
                  <Route path="/zubehoer/software" element={<AccessoryListing category="software" />} />
                  <Route path="/zubehoer/magic-mirror-rahmen" element={<MagicMirrorRahmenListing />} />
                  <Route path="/zubehoer/magic-mirror-rahmen/:slug" element={<MagicMirrorRahmenDetail />} />
                  <Route path="/zubehoer/:category/:slug" element={<ErrorBoundary><AccessoryDetail /></ErrorBoundary>} />
                  <Route path="/zubehoer-legacy" element={<Accessories />} />
                  <Route path="/service" element={<Support />} />
                  <Route path="/support" element={<Navigate to="/service" replace />} />
                  <Route path="/showroom" element={<Showroom />} />
                  <Route path="/warenkorb" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/checkout/success" element={<CheckoutSuccess />} />
                  <Route path="/intern/produktions-szenen" element={<ProductionScenesPreview />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/kontakt" element={<Contact />} />
                  <Route path="/bestpreisgarantie" element={<Bestpreis />} />
                  <Route path="/bestpreis" element={<Navigate to="/bestpreisgarantie" replace />} />
                  <Route path="/angebot/:token" element={<QuoteView />} />
                  <Route path="/ueber-uns" element={<About />} />
                  {/* Rechtstexte */}
                  <Route path="/gewaehrleistung" element={<Gewaehrleistung />} />
                  <Route path="/impressum" element={<Impressum />} />
                  <Route path="/datenschutz" element={<Datenschutz />} />
                  <Route path="/agb" element={<AGB />} />
                  <Route path="/widerrufsbelehrung" element={<Widerrufsbelehrung />} />
                  <Route path="/widerrufen" element={<Withdrawal />} />
                  <Route path="/kuendigen" element={<Kuendigung />} />
                  <Route path="/en/cancel" element={<Kuendigung lang="en" />} />
                  <Route path="/nl/opzeggen" element={<Kuendigung lang="nl" />} />
                  <Route path="/fotobox-pro-academy" element={<AcademyLanding />} />
                  <Route path="/kreulbooth" element={<KreulBooth />} />
                  <Route path="/zahlungsarten" element={<Zahlungsarten />} />
                  <Route path="/software" element={<Navigate to="/kreulbooth" replace />} />
                  {/* Englischer Bereich für internationale Kunden */}
                  <Route path="/werkstatt" element={<Werkstatt />} />
                  <Route path="/live" element={<Live />} />
                  <Route path="/einkauf" element={<EinkaufApp />} />
                  <Route path="/zentrale" element={<Zentrale />} />
                  <Route path="/en" element={intl("en", IntlHome)} />
                  <Route path="/en/photo-booths" element={intl("en", IntlProducts)} />
                  <Route path="/en/photo-booths/:slug" element={intl("en", IntlProductDetail)} />
                  <Route path="/en/why-us" element={intl("en", IntlWhyUs)} />
                  <Route path="/en/faq" element={intl("en", IntlFaq)} />
                  <Route path="/en/contact" element={intl("en", IntlContact)} />
                  <Route path="/en/kreulbooth" element={intl("en", KreulBooth)} />
                  <Route path="/en/cart" element={intl("en", Cart)} />
                  <Route path="/en/checkout" element={intl("en", Checkout)} />
                  <Route path="/en/checkout/success" element={intl("en", CheckoutSuccess)} />
                  {/* Niederländischer Bereich (Niederlande und Flandern) */}
                  <Route path="/nl" element={intl("nl", IntlHome)} />
                  <Route path="/nl/fotoboxen" element={intl("nl", IntlProducts)} />
                  <Route path="/nl/fotoboxen/:slug" element={intl("nl", IntlProductDetail)} />
                  <Route path="/nl/waarom-wij" element={intl("nl", IntlWhyUs)} />
                  <Route path="/nl/faq" element={intl("nl", IntlFaq)} />
                  <Route path="/nl/contact" element={intl("nl", IntlContact)} />
                  <Route path="/nl/kreulbooth" element={intl("nl", KreulBooth)} />
                  <Route path="/nl/winkelwagen" element={intl("nl", Cart)} />
                  <Route path="/nl/afrekenen" element={intl("nl", Checkout)} />
                  <Route path="/nl/afrekenen/bedankt" element={intl("nl", CheckoutSuccess)} />
                  <Route path="/nl/fotobox-kopen/:city" element={intl("nl", IntlCity)} />
                  <Route path="/academy" element={<Navigate to="/fotobox-pro-academy" replace />} />
                  <Route path="/vertrag-widerrufen" element={<Navigate to="/widerrufen" replace />} />
                  {/* Alte Shopify-Legal-URLs → aktuelle Rechtstexte */}
                  <Route path="/policies/legal-notice" element={<Navigate to="/impressum" replace />} />
                  <Route path="/policies/privacy-policy" element={<Navigate to="/datenschutz" replace />} />
                  <Route path="/policies/terms-of-service" element={<Navigate to="/agb" replace />} />
                  <Route path="/policies/refund-policy" element={<Navigate to="/widerrufsbelehrung" replace />} />
                  <Route path="/policies/shipping-policy" element={<Navigate to="/agb" replace />} />
                  <Route path="/widerruf" element={<Navigate to="/widerrufsbelehrung" replace />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/registrieren" element={<Register />} />
                  <Route path="/konto" element={<Protected><AccountLayout /></Protected>}>
                    <Route index element={<AccountOverview />} />
                    <Route path="bestellungen" element={<AccountOrders />} />
                    <Route path="bestellungen/:id" element={<AccountOrderDetail />} />
                    <Route path="abos" element={<AccountSubscriptions />} />
                    <Route path="link-bestellung" element={<AccountLinkOrder />} />
                    <Route path="profil" element={<AccountProfile />} />
                  </Route>
                  <Route path="/admin" element={<Protected adminOnly><AdminLayout /></Protected>}>
                    <Route index element={<Navigate to="/admin/bestellungen" replace />} />
                    <Route path="uebersicht" element={<AdminDashboard />} />
                    <Route path="auftraege" element={<AdminWorkorders />} />
                    <Route path="auftraege/:id" element={<AdminWorkorderDetail />} />
                    <Route path="aufgaben" element={<AdminTasks />} />
                    <Route path="kalender" element={<AdminCalendar />} />
                    <Route path="produkte" element={<AdminProducts />} />
                    <Route path="produkte/:id" element={<AdminProductDetail />} />
                    <Route path="komponenten" element={<AdminComponents />} />
                    <Route path="komponenten/:id" element={<AdminComponentDetail />} />
                    <Route path="zubehoer" element={<AdminAccessories />} />
                    <Route path="zubehoer/:id" element={<AdminAccessoryEdit />} />
                    <Route path="abos" element={<AdminSubscriptions />} />
                    <Route path="angebote" element={<AdminQuotes />} />
                    <Route path="angebote/:id" element={<AdminQuoteDetail />} />
                    <Route path="bestpreis" element={<AdminBestpreis />} />
                    <Route path="widerrufe" element={<AdminWithdrawals />} />
                    <Route path="site-config" element={<AdminSiteConfig />} />
                    <Route path="einstellungen/dokumente" element={<AdminDocumentSettings />} />
                    <Route path="einstellungen/zahlungsarten" element={<AdminPaymentFees />} />
                    <Route path="einstellungen/ausland" element={<AdminIntlShipping />} />
                    <Route path="einstellungen/nordfoto" element={<AdminNordfotoHealth />} />
                    <Route path="einstellungen/sicherheit" element={<AdminSecurity />} />
                    <Route path="foto-kreul" element={<AdminFotoKreulCatalog />} />
                    <Route path="bestellungen/neu" element={<AdminOrderFromDocument />} />
                    <Route path="aktion" element={<AdminPromotion />} />
                    <Route path="cross-selling" element={<AdminCrossSelling />} />
                    <Route path="website" element={<AdminPagesList />} />
                    <Route path="website/:id" element={<AdminPageEditor />} />
                    <Route path="einkauf" element={<AdminEinkauf />} />
                    <Route path="lieferanten" element={<Navigate to="/admin/einkauf?tab=lieferanten" replace />} />
                    <Route path="lieferanten/:id" element={<AdminSupplierDetail />} />
                    <Route path="bestellungen" element={<AdminOrders />} />
                    <Route path="bestellungen/:id" element={<AdminOrderDetail />} />
                    <Route path="kunden" element={<AdminCustomers />} />
                    <Route path="kunden/:id" element={<AdminCustomerDetail />} />
                    <Route path="einstellungen" element={<AdminOpsSettings />} />
                  </Route>
                </Route>
              </Routes>
                </Suspense>
              <BestpreisFloatingBanner />
              <ConsentBanner />
            </BrowserRouter>
          </CartProvider>
        </ConsentProvider>
      </AuthProvider>
      </HelmetProvider>
      </LazyMotion>
    </div>
  );
}

export default App;
